// The frame of a workspace chat (Lane 1355, moved from the sourcing event chat
// panel of Lane 1322 without a change in look or behavior): header, the thread
// as safe Markdown with the live draft and progress line, Try again, and the
// message box. Each workspace supplies its own cards through `renderCards`.
import React, { useEffect, useRef, useState } from 'react';
import ChatMarkdown from '../ChatMarkdown';
import './workspaceChat.css';

// `draft` / `onDraft` (optional): hold the unsent message outside the panel, so it
// survives the panel being unmounted (the deck chat in Present or a layout switch).
// `headerActions` (optional): controls shown before the close button (the page's
// "Feedback" while the chat is open; Lane 1382, ChatOpenButton.js).
export default function WorkspaceChatPanel({
  chat, title, ariaLabel, emptyState, unavailableText, placeholder, topNote,
  renderCards, onClose, ask, contextChip, draft, onDraft, headerActions,
}) {
  const [ownText, setOwnText] = useState('');
  const text = onDraft ? draft : ownText;
  const setText = onDraft || setOwnText;
  const setTextRef = useRef(setText);   // the prefill below runs only when `ask` changes
  setTextRef.current = setText;
  const inputRef = useRef(null);
  // Wingman's opening question for a start the workspace wrote (no model call);
  // it goes when the buyer sends (Ryan, sourcing slice 2b).
  const [opening, setOpening] = useState(null);
  useEffect(() => {
    if (!ask || !ask.text) return;
    setTextRef.current(ask.text);
    setOpening(ask.question || null);
    if (inputRef.current) inputRef.current.focus();
  }, [ask]);

  const endRef = useRef(null);
  useEffect(() => {
    if (endRef.current && endRef.current.scrollIntoView) endRef.current.scrollIntoView({ block: 'end' });
  }, [chat.turns, chat.draft]);

  // The text is cleared only when the turn really started (not while another
  // answer is still being written; Codex UI slice 1 round 1).
  const submit = () => {
    if (!text.trim() || chat.busy || chat.answering) return;
    if (chat.send(text)) { setText(''); setOpening(null); }
  };

  return (
    <aside className="wchat-panel" aria-label={ariaLabel}>
      <div className="wchat-head">
        <span className="wchat-strong">{title}</span>
        <span className="wchat-push wchat-row">
          {headerActions}
          <button type="button" className="wchat-x" aria-label="Hide chat" onClick={onClose}>
            <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
          </button>
        </span>
      </div>
      {contextChip}
      <div className="wchat-body">
        {chat.available === null && <div className="wchat-mut">Loading…</div>}
        {chat.available === false && <div className="wchat-notice">{unavailableText}</div>}
        {chat.available && topNote ? topNote : null}
        {chat.available && chat.turns.length === 0 && <div className="wchat-mut">{emptyState}</div>}
        {chat.turns.map((t) => (
          <div key={t.client_turn_id} className="wchat-turn">
            <div className="wchat-q">{t.question}</div>
            {t.answer && <ChatMarkdown content={t.answer} />}
            {t.pending && (
              <>
                {chat.draft ? <ChatMarkdown content={chat.draft} isDraft /> : null}
                <div className="wchat-mut" aria-live="polite">{chat.progress}</div>
              </>
            )}
            {renderCards ? renderCards(t) : null}
            {t.error && (
              <div className="wchat-error" role="alert">
                {t.error}
                <button type="button" className="wchat-clr" disabled={chat.busy} onClick={() => chat.retry(t)}>Try again</button>
              </div>
            )}
          </div>
        ))}
        {opening && (
          <div className="wchat-turn wchat-opening" aria-live="polite">
            <ChatMarkdown content={opening} />
          </div>
        )}
        {chat.answering && (
          <div className="wchat-mut" aria-live="polite">Wingman is still answering your last question. It appears here when it is done.</div>
        )}
        {chat.loadError && <div className="wchat-error" role="alert">{chat.loadError}</div>}
        <div ref={endRef} />
      </div>
      {chat.available && (
        <div className="wchat-input">
          <textarea ref={inputRef} className="wchat-textarea" rows={3} value={text} maxLength={8000}
            placeholder={placeholder} aria-label="Message"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
          <button type="button" className="wchat-btn wchat-btn--primary" disabled={chat.busy || chat.answering || !text.trim()}
            onClick={submit}>{chat.busy ? 'Working…' : 'Send'}</button>
        </div>
      )}
    </aside>
  );
}
